import { create } from 'zustand';
import { cvApi } from '../services/api/cvApi';
import {
  transformCVForStore,
  formatCVForCompletion,
  getDefaultOrdering
} from '../utils/cvUtils';

// Initial state for the Employee CV store
const initialState = {
  currentCV: null,
  employeeCVs: [],
  cvMetadata: null, // Store CV metadata including radarFirstLevelSegments
  isLoading: false,
  isLoadingMore: false,
  isSaving: false,
  error: null,
  selectedVersion: null,
  editMode: false,
  isNewCV: false, // Track if the current CV is new (no ID)
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  orderBy: getDefaultOrdering('employee').orderBy,
  orderDirection: getDefaultOrdering('employee').orderDirection,
  limit: 20
};

/**
 * Zustand store for managing Employee CV operations
 */
const useCVStoreEmployee = create((set, get) => {
  // Helper to update pageInfo after fetches (mirrors cvStoreRepository)
  function buildUpdatedPageInfo(current, response, newItems) {
    if (!response || !response.pageInfo) return { ...initialState.pageInfo };
    return {
      ...response.pageInfo,
      hasNextPage: newItems.length < response.pageInfo.totalCount
    };
  }

  return {
    ...initialState,

    // UI Actions
    setEditMode: (editMode) => set({ editMode }),
    setSelectedVersion: (version) => set({ selectedVersion: version }),

    /**
     * Fetch a specific CV by ID
     * @param {string} cvId - The ID of the CV to fetch
     * @returns {Promise<Object|null>} The fetched CV or null if there was an error
     */
    fetchCVById: async (cvId) => {
      try {
        set({ isLoading: true, error: null });

        const cv = await cvApi.getCVById(cvId);
        if (cv) {
          set({
            currentCV: formatCVForCompletion(cv),
            cvMetadata: cv.metadata || null, // Store metadata from the response
            isNewCV: false, // Existing CV loaded
            error: null,
            isLoading: false
          });
          return cv;
        }

        set({
          error: 'CV not found',
          isLoading: false
        });
        return null;
      } catch (err) {
        console.error('Error fetching CV');
        set({
          error: err.message || 'Failed to load CV',
          isLoading: false
        });
        return null;
      }
    },

    /**
     * Fetch CVs for a specific employee
     * @param {string} employeeId - The employee ID
     * @param {boolean} reset - Whether to reset the list or append to it
     * @returns {Promise<Object|null>} The API response or null if there was an error
     */
    fetchEmployeeCVs: async (employeeId, reset = false) => {
      if (!employeeId) return null;

      try {
        reset ? set({ isLoading: true }) : set({ isLoadingMore: true });

        const { limit, orderBy, orderDirection, employeeCVs, pageInfo } = get();
        const cursor = reset ? null : pageInfo.endCursor;

        const response = await cvApi.getCVsByEmployeeId(employeeId, {
          limit,
          cursor,
          orderBy,
          orderDirection
        });

        if (response) {
          const newItems = reset ? response.items : [...employeeCVs, ...response.items];
          const updatedPageInfo = buildUpdatedPageInfo(pageInfo, response, newItems);
          set({
            employeeCVs: newItems,
            pageInfo: updatedPageInfo,
            error: null,
            isLoading: false,
            isLoadingMore: false
          });
          return response;
        }

        reset ? set({ isLoading: false }) : set({ isLoadingMore: false });
        return null;
      } catch (err) {
        console.error('Error fetching employee CVs');
        set({
          error: err.message || 'Failed to load employee CVs',
          isLoading: false,
          isLoadingMore: false
        });
        return null;
      }
    },

    /**
     * Update a specific CV in the employee store
     * @param {Object} updatedCV - The updated CV object
     * @returns {boolean} Whether the update was successful
     */
    updateCV: (updatedCV) => {
      if (!updatedCV || !updatedCV.id) return false;

      const { employeeCVs, currentCV, pageInfo } = get();
      const index = employeeCVs.findIndex(cv => cv.id === updatedCV.id);

      const existingCV = index !== -1 ? employeeCVs[index] : null;
      const transformedCV = transformCVForStore(updatedCV, existingCV);

      // Update the employeeCVs array
      if (index !== -1) {
        // Update existing CV
        const updatedCVs = [...employeeCVs];
        updatedCVs[index] = transformedCV;
        set({ employeeCVs: updatedCVs });
      } else {
        // Add new CV and increment total count
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1 // Increment total count for new CV
        };
        set({ 
          employeeCVs: [transformedCV, ...employeeCVs],
          pageInfo: updatedPageInfo
        });
      }

      // Also update currentCV if it matches the updated CV's ID
      if (currentCV && currentCV.id === updatedCV.id) {
        set({ currentCV: formatCVForCompletion(transformedCV) });
      }

      return true;
    },

    /**
     * Save a CV
     * @param {Object} cvData - The CV data to save
     * @returns {Promise<Object|null>} The saved CV or null if there was an error
     */
    saveCV: async (cvData) => {
      try {
        set({ isSaving: true, error: null });

        const isNewCV = !cvData.id; // Check if this is a new CV before saving
        const savedCV = await cvApi.saveCV(cvData);

        if (savedCV) {
          // Extract the actual CV data from the API response
          const extractedCVData = savedCV.data || savedCV;
          
          // If we only got an ID back, fetch the complete CV data
          if (extractedCVData.id && Object.keys(extractedCVData).length <= 2) {
            try {
              const completeCV = await cvApi.getCVById(extractedCVData.id);
              
              // Get the current CV to pass as existing CV for version increment
              const { currentCV } = get();
              
              // Transform the complete CV data (this will increment version)
              const transformedCV = transformCVForStore(completeCV, currentCV);
              const formattedCV = formatCVForCompletion(transformedCV);

              set({
                currentCV: formattedCV,
                cvMetadata: completeCV.metadata || null, // Preserve metadata from the complete CV
                isNewCV: false,
                error: null,
                isSaving: false
              });

              // Notify with the complete CV data
              get().notifyCVSaved({ data: completeCV });
              return formattedCV;
              
            } catch (fetchError) {
              console.error('❌ Failed to fetch complete CV data:', fetchError);
              // Fall back to using the partial data
            }
          }
          
          // Get the current CV to pass as existing CV for version increment
          const { currentCV } = get();
          
          // Transform the API response to the store format (this will increment version)
          const transformedCV = transformCVForStore(extractedCVData, currentCV);
          
          // Format for completion calculation
          const formattedCV = formatCVForCompletion(transformedCV);

          set({
            currentCV: formattedCV,
            cvMetadata: extractedCVData.metadata || get().cvMetadata, // Preserve metadata from response or keep existing
            isNewCV: false, // After saving, it's no longer new
            error: null,
            isSaving: false
          });

          // Notify the CV repository store with the API response (not the formatted version)
          get().notifyCVSaved(savedCV);

          return formattedCV;
        }

        set({
          error: 'Failed to save CV',
          isSaving: false
        });

        return null;
      } catch (err) {
        console.error('Error saving CV');
        set({
          error: err.message || 'Failed to save CV',
          isSaving: false
        });

        return null;
      }
    },

    /**
     * Create a new CV
     * @param {Object|null} employeeData - Basic employee data to initialize the CV (null for generic CV)
     * @returns {Object} An empty CV structure with employee data
     */
    createNewCV: (employeeData = null) => {
      const newCV = {
        id: null,
        fullName: employeeData?.fullName || '',
        version: 1,
        employee: employeeData
          ? {
              id: employeeData.id,
              azureId: employeeData.azureId,
              fullName: employeeData.fullName
            }
          : null,
        position: null,
        grade: null,
        professionalSummary: '',
        country: '',
        city: '',
        roles: [],
        skills: [],
        employmentHistory: [],
        education: [],
        courses: [],
        certifications: [],
        languages: []
      };

      const formattedCV = formatCVForCompletion(newCV);
      
      // Set all the state atomically to avoid intermediate null states
      set({ 
        currentCV: formattedCV,
        cvMetadata: null, // Clear metadata for new CVs
        isNewCV: true,
        error: null,
        selectedVersion: null,
        editMode: false
      });

      return formattedCV;
    },

    /**
     * Event handler for CV saved - can be subscribed to by other stores
     * This is intentionally left empty to allow subscription by other stores
     */
    notifyCVSaved: (savedCV) => {
      // Intentionally empty - override point for subscriber stores
    },

    /**
     * Reset store state
     */
    resetState: () => {
      set({ ...initialState });
    },

    /**
     * Helper to update pageInfo after fetches (mirrors cvStoreRepository)
     */
    buildUpdatedPageInfo,

    /**
     * Get the error message
     * @returns {string} The error message
     */
    getError: () => get().error,

    /**
     * Set the error message
     * @param {string} error - The error message
     */
    setError: (error) => set({ error }),

    /**
     * Check if the current CV is new (has no ID)
     * @returns {boolean} True if the current CV is new
     */
    getIsNewCV: () => get().isNewCV,

    /**
     * Update the current CV data
     * @param {Object} updates - Partial CV data to update
     */
    updateCurrentCV: (updates) => {
      const { currentCV } = get();
      if (currentCV) {
        set({
          currentCV: {
            ...currentCV,
            ...updates
          }
        });
      }
    },

    /**
     * Delete a CV
     * @param {string} cvId - The ID of the CV to delete
     * @returns {Promise<boolean>} Whether the deletion was successful
     */
    deleteCV: async (cvId) => {
      try {
        set({ isLoading: true, error: null });

        const response = await cvApi.deleteCV(cvId);
        
        if (response) {
          
          const { employeeCVs, currentCV, pageInfo } = get();
          
          // Remove from employeeCVs list
          const updatedCVs = employeeCVs.filter(cv => cv.id !== cvId);
          
          // Clear currentCV if it's the one being deleted
          const updatedCurrentCV = currentCV && currentCV.id === cvId ? null : currentCV;
          
          // Update page info to reflect the deletion
          const updatedPageInfo = {
            ...pageInfo,
            totalCount: Math.max(0, pageInfo.totalCount - 1) // Decrement total count, but don't go below 0
          };
          
          set({
            employeeCVs: updatedCVs,
            currentCV: updatedCurrentCV,
            pageInfo: updatedPageInfo,
            isNewCV: false,
            error: null,
            isLoading: false
          });

          // Notify other stores about the deletion
          get().notifyCVDeleted(cvId);
          
          return true;
       }

        set({
          error: 'Failed to delete CV',
          isLoading: false
        });

        return false;
      } catch (err) {
        console.error('Error deleting CV');
        set({
          error: err.message || 'Failed to delete CV',
          isLoading: false
        });

        return false;
      }
    },

    /**
     * Event handler for CV deleted - can be subscribed to by other stores
     * This is intentionally left empty to allow subscription by other stores
     */
    notifyCVDeleted: (cvId) => {
      // Intentionally empty - override point for subscriber stores
    },

    /**
     * Get the CV metadata
     * @returns {Object|null} The CV metadata containing radarFirstLevelSegments
     */
    getCVMetadata: () => get().cvMetadata,

  };
});

export default useCVStoreEmployee; 